import { cn } from '@newcore/ui/cn';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import { FavoriteButton } from '@/components/ui/buttons/favorite-button';
import { AppViewMode } from '@/enums/app-view-mode.enums';
import type { UserApplication } from '@/types/api.types';
import type { AppRowLayout } from '@/types/app-view.types';
import { AppIconBox } from './app-icon-box';

const ROW_CLASSES: Record<AppRowLayout, string> = {
  [AppViewMode.CARDS]:
    'rounded-xl border border-secondary bg-primary p-3 pr-12 hover:not-disabled:border-primary hover:not-disabled:shadow-xs',
  [AppViewMode.LIST]:
    'h-14 rounded-lg pl-2 pr-12 hover:not-disabled:bg-secondary_hover',
};

const ICON_BOX_SIZES: Record<AppRowLayout, Sizes.SM | Sizes.MD> = {
  [AppViewMode.CARDS]: Sizes.MD,
  [AppViewMode.LIST]: Sizes.SM,
};

const NAME_WEIGHTS: Record<AppRowLayout, FontWeights> = {
  [AppViewMode.CARDS]: FontWeights.SEMIBOLD,
  [AppViewMode.LIST]: FontWeights.MEDIUM,
};

interface ApplicationRowProps {
  application: UserApplication;
  layout: AppRowLayout;
  onClick: (application: UserApplication) => void;
  isLoading?: boolean;
}

export function ApplicationRow({
  application,
  layout,
  onClick,
  isLoading = false,
}: ApplicationRowProps) {
  const handleClick = () => onClick(application);

  return (
    <div className='group relative w-full'>
      <button
        type='button'
        onClick={handleClick}
        disabled={isLoading}
        title={application.app_name}
        data-testid={`application-row-${application.app_id}`}
        className={cn(
          'flex w-full items-center gap-3 text-left transition-[background-color,border-color,box-shadow] duration-150',
          ROW_CLASSES[layout],
          isLoading && 'opacity-70'
        )}
      >
        <AppIconBox application={application} size={ICON_BOX_SIZES[layout]} />
        <Text
          size={TextSizes.MD}
          weight={NAME_WEIGHTS[layout]}
          className='min-w-0 truncate text-primary'
        >
          {application.app_name}
        </Text>
      </button>
      <div className='absolute right-2 top-1/2 -translate-y-1/2'>
        <FavoriteButton
          applicationId={application.app_id}
          isFavorite={application.is_favorite}
        />
      </div>
    </div>
  );
}
